import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supportedLanguages, type SupportedLanguage } from '@/lib/i18n';

interface LanguageProviderProps {
  children: React.ReactNode;
}

const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const { lang } = useParams<{ lang: SupportedLanguage }>();
  const { i18n } = useTranslation();

  useEffect(() => {
    // Pure language detection: just read from URL and sync i18n
    if (lang && supportedLanguages.includes(lang)) {
      // Update i18n if language changed
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
      
      // Update HTML lang attribute (passive, no SEO)
      if (typeof document !== 'undefined') {
        document.documentElement.lang = lang;
      }
    } else if (lang) {
      // Invalid language in URL, fallback to English
      if (i18n.language !== 'en') {
        i18n.changeLanguage('en');
      }
      if (typeof document !== 'undefined') {
        document.documentElement.lang = 'en';
      }
    } else if (!lang) {
      // Root URL "/" - use language from SSR initial state (defaults to 'en')
      // i18n is already initialized from window.__INITIAL_STATE__ in entry-client.tsx
      const currentLang = i18n.language as SupportedLanguage;
      if (typeof document !== 'undefined' && supportedLanguages.includes(currentLang)) {
        document.documentElement.lang = currentLang;
      }
    }
  }, [lang, i18n]);

  return <>{children}</>;
};

export default LanguageProvider;